import * as React from "react"

import ActionButtonInternalLink from "./actionbutton/ActionButtonInternalLink"
import AttachmentInternalLink from "./attachment/AttachmentInternalLink"
import CollectionInternalLink from "./collection/CollectionInternalLink"
import ConceptInternalLink from "./concept/ConceptInternalLink"
import ItemInternalLink from "./item/ItemInternalLink"
import ParametersInternalLink from "./parameters/ParametersInternalLink"
import PersonInternalLink from "./person/PersonInternalLink"
import ReportInternalLink from "./report/ReportInternalLink"
import ScriptInternalLink from "./script/ScriptInternalLink"
import SiteInternalLink from "./site/SiteInternalLink"
import UnavailableLink from "./UnavailableLink"
import WorkspaceInternalLink from "./workspace/WorkspaceInternalLink"

interface Props {
    url: string
}

// every internal link starts with $url. and closed with $
// every internal link has to contain at least one word: $url.parameter$
// an internal link might contain a second word: $url.workspace.create$
// an internal link might contain one or more number after one or more words:
// $url.item.456$
// $url.report.execute.456$
// $url.smartlist.456.123$
// $url.actionbutton.concept.456.123$
// an internal link can contain a label, which will become the name text content of the link
// $url.item.456#This is the content for the link$
const InternalLink = ({ url }: Props): JSX.Element => {
    // const internalLinksRegex = /(\$url.(\w)+(.\w+)*(.\d+)*(#[^$\n]+)?\$)/g
    // remove the starting $url. and ending $
    const linkWithoutWrapper = url.replace("$url.", "").slice(0, -1)

    // extract the custom label that is split from the link by a #
    const linkSplitByLabelCharacter = linkWithoutWrapper.split("#")
    const linkWithoutLabel = linkSplitByLabelCharacter[0].toLowerCase()
    const customLabel =
        linkSplitByLabelCharacter.length > 1 ? linkSplitByLabelCharacter[1] : ""

    // some resources in the OldUI just use the url content as the label
    // we keep this functionality for now
    const label = !!customLabel ? customLabel : `url.${linkWithoutLabel}`

    const attributes: Record<string, string> = {
        "data-ft-link": url,
        "data-ft-link-type": "internal",
    }

    const linkParameters = linkWithoutLabel.split(".")

    let linkComponent = <></>

    if (linkParameters.length > 0) {
        switch (linkParameters[0]) {
            case "actionbutton":
                linkComponent = (
                    <ActionButtonInternalLink
                        attributes={attributes}
                        internalLink={url}
                        label={label}
                        linkParameters={linkParameters}
                    />
                )
                break
            // item urls and attachments are the same resource
            case "attachment":
            case "itemurl":
                linkComponent = (
                    <AttachmentInternalLink
                        attributes={attributes}
                        label={label}
                        customLabel={customLabel}
                        linkParameters={linkParameters}
                    />
                )
                break
            case "item":
                linkComponent = (
                    <ItemInternalLink
                        attributes={attributes}
                        customLabel={customLabel}
                        label={label}
                        linkParameters={linkParameters}
                    />
                )
                break
            case "list":
                linkComponent = (
                    <ConceptInternalLink
                        attributes={attributes}
                        customLabel={customLabel}
                        label={label}
                        linkParameters={linkParameters}
                    />
                )
                break
            case "parameters":
                linkComponent = (
                    <ParametersInternalLink
                        attributes={attributes}
                        label={label}
                    />
                )
                break
            case "person":
                linkComponent = (
                    <PersonInternalLink
                        attributes={attributes}
                        label={label}
                        linkParameters={linkParameters}
                    />
                )
                break
            case "report":
                linkComponent = (
                    <ReportInternalLink
                        attributes={attributes}
                        label={label}
                        linkParameters={linkParameters}
                    />
                )
                break
            case "script":
                linkComponent = (
                    <ScriptInternalLink
                        attributes={attributes}
                        label={label}
                        linkParameters={linkParameters}
                    />
                )
                break
            case "site":
                linkComponent = (
                    <SiteInternalLink attributes={attributes} label={label} />
                )
                break
            case "smartlist":
                linkComponent = (
                    <CollectionInternalLink
                        attributes={attributes}
                        customLabel={customLabel}
                        label={label}
                        linkParameters={linkParameters}
                    />
                )
                break
            case "workspace":
                linkComponent = (
                    <WorkspaceInternalLink
                        attributes={attributes}
                        customLabel={customLabel}
                        label={label}
                        linkParameters={linkParameters}
                    />
                )
                break
            default:
                linkComponent = (
                    <UnavailableLink
                        attributes={attributes}
                        label={customLabel}
                    />
                )
                break
        }
    }

    return linkComponent
}

export default InternalLink
